import { useState, useCallback, useRef, useEffect } from 'react';
import webSocketService from '../services/websocket.service';
import projectService from '../services/project.service';
import { 
  WEBSOCKET_CONFIG, 
  SYNC_STATES, 
  WS_EVENTS, 
  PROGRESS_MESSAGES,
  ERROR_MESSAGES,
  TIMEOUTS 
} from '../utils/websocketConstants';

const useWebSocketSync = (authToken) => {
  const [connectionState, setConnectionState] = useState({
    isConnected: false,
    isConnecting: false,
    error: null,
    reconnectAttempts: 0
  });

  // Changed to Map to support multiple concurrent syncs
  // Key: jobId, Value: sync state object
  const [syncStates, setSyncStates] = useState({});

  const connectionTimeoutRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Initialize WebSocket connection
  const initializeConnection = useCallback(async () => {
    if (!WEBSOCKET_CONFIG.ENABLED || !authToken) {
      return false;
    }

    if (connectionState.isConnected || connectionState.isConnecting) {
      return true;
    }

    setConnectionState(prev => ({
      ...prev,
      isConnecting: true,
      error: null
    }));

    try {
      // Set connection timeout
      connectionTimeoutRef.current = setTimeout(() => {
        setConnectionState(prev => ({
          ...prev,
          isConnecting: false,
          error: ERROR_MESSAGES.WEBSOCKET_TIMEOUT
        }));
      }, TIMEOUTS.WEBSOCKET_CONNECTION);

      // Socket.IO will automatically append /socket.io/ to the URL
      // We only need the base URL here
      const websocketUrl = WEBSOCKET_CONFIG.URL;
      
      await webSocketService.connect(websocketUrl, authToken, WEBSOCKET_CONFIG.NAMESPACE);

      // Clear timeout on successful connection
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      setConnectionState(prev => ({
        ...prev,
        isConnected: true,
        isConnecting: false,
        error: null,
        reconnectAttempts: 0
      }));

      return true;

    } catch (error) {
      console.error('❌ WebSocket connection failed:', error);
      
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      setConnectionState(prev => ({
        ...prev,
        isConnected: false,
        isConnecting: false,
        error: error.message || ERROR_MESSAGES.WEBSOCKET_CONNECTION_FAILED,
        reconnectAttempts: prev.reconnectAttempts + 1
      }));

      return false;
    }
  }, [authToken, connectionState.isConnected, connectionState.isConnecting]);

  // Setup WebSocket event handlers
  const setupEventHandlers = useCallback(() => {
    if (!webSocketService.isConnected) {
      console.error('⚠️ Cannot setup event handlers - WebSocket not connected');
      return;
    }

    // Sync progress event
    webSocketService.on(WS_EVENTS.SYNC_PROGRESS, (data) => {
      const { jobId, progress, message } = data;
      
      setSyncStates(prev => {
        // Only update if this jobId exists (has been started)
        if (!prev[jobId]) return prev;
        
        return {
          ...prev,
          [jobId]: {
            ...prev[jobId],
            status: SYNC_STATES.SYNCING,
            progress,
            message: message || PROGRESS_MESSAGES[progress] || 'Processing...'
          }
        };
      });
    });

    // Lock waiting event - job is waiting for board lock
    webSocketService.on('sync_lock_waiting', (data) => {
      const { jobId, waitInfo } = data;
      
      setSyncStates(prev => {
        if (!prev[jobId]) return prev;
        
        return {
          ...prev,
          [jobId]: {
            ...prev[jobId],
            status: 'waiting',
            progress: 5,
            message: `Waiting for previous sync... ${waitInfo?.remaining || '?'}s remaining`,
            waitInfo
          }
        };
      });
    });

    // Lock acquired event - lock obtained, sync starting
    webSocketService.on('sync_lock_acquired', (data) => {
      const { jobId } = data;
      
      setSyncStates(prev => {
        if (!prev[jobId]) return prev;
        
        return {
          ...prev,
          [jobId]: {
            ...prev[jobId],
            status: SYNC_STATES.SYNCING,
            progress: 10,
            message: 'Lock acquired, sync starting...',
            waitInfo: null
          }
        };
      });
    });

    // Sync completed event
    webSocketService.on(WS_EVENTS.SYNC_COMPLETE, (data) => {
      const { jobId, result } = data;

      setSyncStates(prev => {
        if (!prev[jobId]) return prev;
        
        return {
          ...prev,
          [jobId]: {
            ...prev[jobId],
            status: SYNC_STATES.COMPLETED,
            progress: 100,
            message: PROGRESS_MESSAGES[100],
            result,
            error: null,
            waitInfo: null
          }
        };
      });
    });

    // Sync error event
    webSocketService.on(WS_EVENTS.SYNC_ERROR, (data) => {
      const { jobId, error } = data;
      console.error(`❌ [${jobId}] Sync error:`, data);

      setSyncStates(prev => {
        if (!prev[jobId]) return prev;
        
        return {
          ...prev,
          [jobId]: {
            ...prev[jobId],
            status: SYNC_STATES.ERROR,
            error: error || ERROR_MESSAGES.SERVER_ERROR,
            message: `Error: ${error || 'Unknown error occurred'}`
          }
        };
      });
    });

    // Room joined confirmation (correct event name from backend)
    webSocketService.on('joined_job', (data) => {
      // Silent - room joined successfully
    });

    // Connection events
    webSocketService.on('disconnect', () => {
      setConnectionState(prev => ({
        ...prev,
        isConnected: false
      }));
    });

    webSocketService.on('connect', () => {
      setConnectionState(prev => ({
        ...prev,
        isConnected: true,
        error: null
      }));
    });
  }, []);

  // Start sync process
  const startSync = useCallback(async (boardId, boardName) => {
    try {
      
      // Ensure WebSocket connection
      const connected = await initializeConnection();
      
      if (!connected) {
        throw new Error(ERROR_MESSAGES.WEBSOCKET_CONNECTION_FAILED);
      }

      // Call API to start sync using projectService (returns job ID immediately)
      const response = await projectService.synchronizeProject(boardId);
      
      // Extract jobId and websocketRoom from response
      const jobId = response.data?.result?.jobId;
      // const websocketRoom = response.data?.result?.websocketRoom;
      
      if (!jobId) {
        throw new Error('No job ID returned from API');
      }

      // Initialize sync state for this jobId
      setSyncStates(prev => ({
        ...prev,
        [jobId]: {
          status: SYNC_STATES.SYNCING,
          progress: 0,
          message: 'Sync job created, waiting for updates...',
          jobId,
          boardId,
          boardName,
          startTime: new Date(),
          error: null,
          result: null,
          waitInfo: null
        }
      }));

      // IMPORTANT: Backend adds 'job_' prefix, so we must send jobId without any prefix
      // Frontend sends: "sync_7904159271_xxx" 
      // Backend converts to: "job_sync_7904159271_xxx"
      // Backend emits to: "job_sync_7904159271_xxx"
      const roomIdToJoin = jobId; // Use jobId directly (e.g., "sync_xxx")
      
      try {
        await webSocketService.joinRoom(roomIdToJoin);
      } catch (joinError) {
        console.error('⚠️ Room join failed:', joinError);
        // Continue anyway - server might still send updates
      }

      // NOTE: Sync timeout disabled to allow large projects with many tasks to sync
      // Large projects may take more than 10 minutes to complete sync
      // The sync will only complete when backend sends 'sync_complete' or 'sync_error' event
      
      return { jobId, boardId, boardName };

    } catch (error) {
      console.error('❌ Failed to start sync:', error);
      // Note: Without jobId, we can't update specific state, 
      // so this error handling is for the initiation phase only
      throw error;
    }
  }, [initializeConnection]);

  // Cancel sync process for specific jobId
  const cancelSync = useCallback((jobId) => {
    if (!jobId) {
      console.error('⚠️ cancelSync called without jobId');
      return;
    }
    
    webSocketService.leaveRoom(jobId);

    setSyncStates(prev => {
      if (!prev[jobId]) return prev;
      
      return {
        ...prev,
        [jobId]: {
          ...prev[jobId],
          status: SYNC_STATES.CANCELLED,
          message: 'Sync cancelled by user'
        }
      };
    });
  }, []);

  // Reset sync state for specific jobId (or all if no jobId)
  const resetSync = useCallback((jobId = null) => {
    if (jobId) {
      // Reset specific job
      setSyncStates(prev => {
        const newState = { ...prev };
        delete newState[jobId];
        return newState;
      });
    } else {
      // Reset all syncs
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      setSyncStates({});
    }
  }, []);

  // Get sync state for specific jobId
  const getSyncState = useCallback((jobId) => {
    return syncStates[jobId] || null;
  }, [syncStates]);

  // Retry connection
  const retryConnection = useCallback(() => {
    if (connectionState.reconnectAttempts < WEBSOCKET_CONFIG.RECONNECTION_ATTEMPTS) {
      retryTimeoutRef.current = setTimeout(() => {
        initializeConnection();
      }, TIMEOUTS.RETRY_DELAY);
    }
  }, [connectionState.reconnectAttempts, initializeConnection]);

  // Initialize on mount - ONLY run once when authToken is available
  useEffect(() => {
    // Skip if already initialized or no auth token
    if (isInitializedRef.current || !authToken || !WEBSOCKET_CONFIG.ENABLED) {
      return;
    }

    // Mark as initialized to prevent re-initialization
    isInitializedRef.current = true;
    
    // Initialize connection
    const doInit = async () => {
      try {
        const connected = await initializeConnection();
        if (connected) {
          setupEventHandlers();
        }
      } catch (error) {
        console.error('❌ WebSocket initialization error:', error);
      }
    };
    
    doInit();

    // Cleanup only on unmount
    return () => {
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      
      if (webSocketService.isConnected) {
        webSocketService.disconnect();
      }
      isInitializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount

  // Reconnect to existing job room (for restored syncs after page refresh)
  const reconnectToRoom = useCallback(async (jobId, boardId, boardName) => {
    try {
      // Ensure WebSocket connection
      const connected = await initializeConnection();
      
      if (!connected) {
        throw new Error(ERROR_MESSAGES.WEBSOCKET_CONNECTION_FAILED);
      }

      // Initialize sync state for this jobId if not exists
      setSyncStates(prev => {
        // Don't overwrite if already exists
        if (prev[jobId]) {
          return prev;
        }
        
        return {
          ...prev,
          [jobId]: {
            status: SYNC_STATES.SYNCING,
            progress: 0,
            message: 'Reconnecting to sync job...',
            jobId,
            boardId,
            boardName,
            startTime: new Date(),
            error: null,
            result: null,
            waitInfo: null
          }
        };
      });

      // Join the WebSocket room
      const roomIdToJoin = jobId;
      
      try {
        await webSocketService.joinRoom(roomIdToJoin);
      } catch (joinError) {
        console.error('⚠️ Room rejoin failed:', joinError);
      }

      return { jobId, boardId, boardName };

    } catch (error) {
      console.error('❌ Failed to reconnect to room:', error);
      throw error;
    }
  }, [initializeConnection]);

  return {
    // State
    connectionState,
    syncStates, // Map of jobId -> sync state
    
    // Actions
    startSync,
    cancelSync,
    resetSync,
    getSyncState, // Get state for specific jobId
    retryConnection,
    initializeConnection,
    reconnectToRoom, // New: reconnect to existing job
    
    // Connection status
    hasWebSocket: WEBSOCKET_CONFIG.ENABLED && connectionState.isConnected
  };
};

export default useWebSocketSync;